// ABOUTME: Standalone day-totals KPI strip (processed orders + returns + inbounds) for embedding in a BI
// ABOUTME: dashboard cell. Self-fetching: reads the shared day-totals date from redux and runs its own query.
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { tokens } from "../../../../../theme";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import SHQuickAction from "../../../../../components/theme/Modal/SHQuickAction";
import { formatNumber } from "../../../../../utils/helpers";
import { useGetDaytotalsAllQuery } from "../../../../../newapi/reports/fulfillment/reportFulfillmentSlice";

// Makes an order total hoverable: the popover lists the same orders per stream and per shipper.
// `breakdown` is { streams: [{ id, name, count }], shippers: [...] } from the day-totals response.
// `children` is a render function receiving the hover props to spread on the trigger element;
// without a breakdown (past date, preview) it receives no props and the total stays plain.
export const OrdersBreakdownQuickAction = ({ breakdown, children }) => {
    const { t, i18n } = useTranslation();

    const hasBreakdown = breakdown?.streams?.length > 0 || breakdown?.shippers?.length > 0;
    if (!hasBreakdown) return children({});

    const column = (title, rows) => (
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 180, flex: 1 }}>
            <Text bold sx={{ mb: 1 }}>{title}</Text>
            {rows.map((row) => (
                <Box
                    key={row.id ?? "unknown"}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 3, py: 0.5 }}
                >
                    <Text noBreak>{row.name ?? t("unknown")}</Text>
                    <Text black>{formatNumber(i18n, row.count)}</Text>
                </Box>
            ))}
        </Box>
    );

    return (
        <SHQuickAction
            maxWidth={600}
            component={
                <Box sx={{ display: "flex", gap: 4, pt: 1, px: 1 }}>
                    {column(t("streams"), breakdown.streams ?? [])}
                    {column(t("shippers"), breakdown.shippers ?? [])}
                </Box>
            }
        >
            {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => children({
                "aria-owns": anchorEl ? "simple-popover" : undefined,
                "aria-haspopup": "true",
                onMouseEnter,
                onMouseLeave,
                onTouchStart,
            })}
        </SHQuickAction>
    );
};

// `sx` is merged into the root so a dashboard cell can restyle/space the element.
export const DaytotalsKpiStrip = ({ sx, preview }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const daytotalsDates = useSelector((state) => state.dayTotalsReport);
    // Open/picked counts only exist for the live day; a past date shows processed orders alone.
    const isToday = daytotalsDates?.startDate === format(new Date(), "yyyy-MM-dd");
    const { data, isLoading, isFetching } = useGetDaytotalsAllQuery(
        { "date": daytotalsDates?.startDate },
        { pollingInterval: 300000, skip: preview }
    );

    // Without a number (query skipped in preview, or a key missing from the response) formatNumber
    // yields "NaN" — show a dash instead.
    const kpi = (value) => {
        if (typeof value !== "number" || isNaN(value)) return "-";
        return formatNumber(i18n, value);
    };

    return (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap", ...sx }}>
            {/* Processed orders + open / picked */}
            <Box>
                <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.processed}>
                    {(hoverProps) => (
                        <Box {...hoverProps} sx={{ width: "fit-content" }}>
                            <Subheading sx={{ fontWeight: "normal" }}>{t("processed_orders")}</Subheading>
                            <Box sx={{ mt: 1, display: 'flex', alignItems: "center" }}>
                                <Typography
                                    sx={{
                                        lineHeight: "32px",
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 32,
                                        fontWeight: "1000",
                                        color: colors.txt["primary"]
                                    }}
                                >
                                    {!isLoading && !isFetching && kpi(data?.orders?.processed)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </OrdersBreakdownQuickAction>
                {isToday && (
                <Box
                    sx={{
                        height: 40,
                        p: 0.5,
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: 'center',
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                        border: `1px solid ${colors.grey[100]}`,
                        boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius: 5,
                        mt: 1,
                    }}
                >
                    <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.open}>
                    {(hoverProps) => (
                    <Box {...hoverProps} sx={{ px: 0.5, height: 32, display: "flex", justifyContent: "flex-start", alignItems: 'center' }}>
                        <Box
                            sx={{
                                height: 24,
                                width: 24,
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: 'center',
                                background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                borderRadius: 3,
                                mr: 1,
                            }}
                        />
                        <Text sx={{ mr: 3 }}>{t("to_process_today")}</Text>
                        <Text black>{!isLoading && !isFetching && kpi(data?.orders?.open)}</Text>
                    </Box>
                    )}
                    </OrdersBreakdownQuickAction>

                    {/* Picked */}
                    <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.picked}>
                    {(hoverProps) => (
                    <Box
                        {...hoverProps}
                        sx={{
                            ml: 2,
                            px: 0.5,
                            height: 32,
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: 'center',
                            background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                            borderRadius: 4,
                            pr: 2,
                        }}
                    >
                        <Box
                            sx={{
                                height: 24,
                                width: 24,
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: 'center',
                                background: colors.green[100],
                                borderRadius: 3,
                                mr: 1,
                            }}
                        />
                        <Text noBreak sx={{ mr: 3 }}>{t("of_which_picked")}</Text>
                        <Text black>{!isLoading && !isFetching && kpi(data?.orders?.picked)}</Text>
                    </Box>
                    )}
                    </OrdersBreakdownQuickAction>
                </Box>
                )}
            </Box>

            {/* Returns + inbounds */}
            <Box
                sx={{
                    minWidth: 200,
                    display: "flex",
                    gap: 2,
                    '@media screen and (max-width: 47.9375em)': {
                        width: 1,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridTemplateRows: 'repeat(2, auto)',
                        gap: 2,
                    },
                    '@media screen and (max-width: 25em)': {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Returns */}
                <Box
                    sx={{
                        py: 1,
                        px: 1,
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                        border: `1px solid ${colors.grey[100]}`,
                        boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius: 5,
                        mt: 1,
                    }}
                >
                    <Text bold sx={{ pl: 1.5, pt: 1, }}>{t("returns")}</Text>
                    <Box sx={{ pt: 1.5, display: "flex", gap: 1 }}>
                        <Box
                            sx={{
                                borderRadius: 4,
                                px: 1.5,
                                py: 1.5,
                                background: colors.green[100],
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 100,
                                "@media screen and (max-width:47.9375em)": { flex: 1 },
                            }}
                        >
                            <Text>{t("processed")}</Text>
                            <Text black variant="green">{!isLoading && !isFetching && kpi(data?.returns?.processed)}</Text>
                        </Box>
                        <Box
                            sx={{
                                borderRadius: 4,
                                px: 1.5,
                                py: 1.5,
                                background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 100,
                            }}
                        >
                            <Text>{t("announced")}</Text>
                            <Text black variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{!isLoading && !isFetching && kpi(data?.returns?.received)}</Text>
                        </Box>
                    </Box>
                </Box>

                {/* Inbounds */}
                <Box
                    sx={{
                        py: 1,
                        px: 1,
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                        border: `1px solid ${colors.grey[100]}`,
                        boxShadow: `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius: 5,
                        mt: 1,
                        "@media screen and (max-width:47.9375em)": { mt: 0 },
                    }}
                >
                    <Text bold sx={{ pl: 1.5, pt: 1, }}>{t("inbounds")}</Text>
                    <Box sx={{ pt: 1.5, display: "flex", gap: 1 }}>
                        <Box
                            sx={{
                                borderRadius: 4,
                                px: 1.5,
                                py: 1.5,
                                background: colors.green[100],
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 100,
                                "@media screen and (max-width:47.9375em)": { flex: 1 },
                            }}
                        >
                            <Text>{t("processed")}</Text>
                            <Text black variant={"green"}>{!isLoading && !isFetching && kpi(data?.inbounds?.processed)}</Text>
                        </Box>
                        <Box
                            sx={{
                                borderRadius: 4,
                                px: 1.5,
                                py: 1.5,
                                background: colors.pink[100],
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Text>{t("awaiting")}</Text>
                            <Text black variant="pink">{!isLoading && !isFetching && kpi(data?.inbounds?.expected)}</Text>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
